import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';

interface DownloadProduct {
  id: number | string;
  name: string;
  category: string;
  image: string;
  fileName?: string;
  fileSize?: string;
  fileData?: string;
  fileUrl?: string;
  purchasedAt?: string;
}

export default function DownloadsPage() {
  const [orderEmail, setOrderEmail] = useState('');
  const [orderLookup, setOrderLookup] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupDone, setLookupDone] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | string | null>(null);
  const [downloadedIds, setDownloadedIds] = useState<Set<number | string>>(new Set());

  // Load purchased products from localStorage
  const purchasedItems: DownloadProduct[] = (() => {
    try {
      const cart = localStorage.getItem('purchasedProducts');
      if (cart) return JSON.parse(cart);

      // Check if there's a completed order in localStorage
      const lastOrder = localStorage.getItem('lastOrder');
      if (lastOrder) {
        const order = JSON.parse(lastOrder);
        return order.items?.map((item: any) => ({
          id: item.product?.id || item.id,
          name: item.product?.name || item.name,
          category: item.product?.category || item.category,
          image: item.product?.image || item.image,
          fileName: item.product?.fileName || item.fileName,
          fileSize: item.product?.fileSize || item.fileSize,
          fileData: item.product?.fileData || item.fileData,
          purchasedAt: new Date().toLocaleDateString(),
        })) || [];
      }
      return [];
    } catch {
      return [];
    }
  })();

  const handleLookup = async () => {
    setIsLookingUp(true);
    await new Promise((r) => setTimeout(r, 1200));
    setIsLookingUp(false);
    setLookupDone(true);
  };

  const handleDownload = async (product: DownloadProduct) => {
    setDownloadingId(product.id);
    await new Promise((r) => setTimeout(r, 800));

    try {
      if (product.fileData && product.fileData.startsWith('data:')) {
        const link = document.createElement('a');
        link.href = product.fileData;
        link.download = product.fileName || `${product.name}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (product.fileUrl) {
        const link = document.createElement('a');
        link.href = product.fileUrl;
        link.download = product.fileName || product.name;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Show info if no file attached
        const info = document.createElement('div');
        info.className =
          'fixed top-4 right-4 bg-amber-500 text-white px-6 py-3 rounded-lg z-50 text-sm max-w-xs';
        info.innerHTML =
          '<i class="ri-information-line mr-2"></i>File is being processed. Check your email for the download link.';
        document.body.appendChild(info);
        setTimeout(() => info.remove(), 4000);
      }

      setDownloadedIds((prev) => new Set([...prev, product.id]));
    } finally {
      setDownloadingId(null);
    }
  };

  const categoryIcon: Record<string, string> = {
    eBooks: 'ri-book-2-line',
    'Image Libraries': 'ri-image-2-line',
    'Mini Videos': 'ri-video-line',
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <Header />

      {/* Hero */}
      <section
        className="relative py-20 overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-20 w-64 h-64 rounded-full bg-teal-400 filter blur-3xl"></div>
          <div className="absolute bottom-10 right-20 w-80 h-80 rounded-full bg-emerald-400 filter blur-3xl"></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-500/20 rounded-2xl mb-6">
            <i className="ri-download-cloud-2-line text-3xl text-teal-400"></i>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4 tracking-tight">
            My Downloads
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            Access all your purchased digital products instantly. Your files are ready to download anytime.
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12">

        {/* Order Lookup */}
        <Card className="p-8 mb-10 border border-slate-200">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-xl flex-shrink-0">
              <i className="ri-search-eye-line text-teal-600 text-lg"></i>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Find Your Order</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                Enter your order ID or email address to access your purchases
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder="Order ID (e.g. ORD-1234567890)"
              value={orderLookup}
              onChange={(e) => setOrderLookup(e.target.value)}
              className="flex-1 px-4 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
            />
            <input
              type="email"
              placeholder="Or enter your email"
              value={orderEmail}
              onChange={(e) => setOrderEmail(e.target.value)}
              className="flex-1 px-4 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
            />
            <Button
              onClick={handleLookup}
              disabled={isLookingUp || (!orderLookup && !orderEmail)}
              className="bg-teal-600 hover:bg-teal-700 text-white whitespace-nowrap px-6"
            >
              {isLookingUp ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Looking up...
                </>
              ) : (
                <>
                  <i className="ri-search-line"></i>
                  Find Order
                </>
              )}
            </Button>
          </div>

          {lookupDone && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
              <i className="ri-information-line text-amber-600"></i>
              <p className="text-sm text-amber-700">
                Order lookup requires an active account. Please{' '}
                <Link to="/login" className="underline font-medium">
                  sign in
                </Link>{' '}
                to view your full purchase history, or check your email for direct download links.
              </p>
            </div>
          )}
        </Card>

        {/* Downloaded Products from localStorage */}
        {purchasedItems.length > 0 ? (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-slate-800">
                Your Purchases
                <span className="ml-2 text-sm font-normal text-slate-500">
                  ({purchasedItems.length} item{purchasedItems.length !== 1 ? 's' : ''})
                </span>
              </h2>
            </div>

            <div className="space-y-4">
              {purchasedItems.map((product) => (
                <Card key={product.id} className="p-6 border border-slate-200">
                  <div className="flex items-center gap-5">
                    {/* Thumbnail */}
                    <div className="w-20 h-16 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover object-top"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            'https://public.readdy.ai/ai/img_res/043d914a604e32ea14a3aa9fa9892ae2.jpg';
                        }}
                      />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i
                            className={`${categoryIcon[product.category] || 'ri-file-line'} text-teal-600 text-sm`}
                          ></i>
                        </div>
                        <span className="text-xs font-medium text-teal-600 uppercase tracking-wide">
                          {product.category}
                        </span>
                      </div>
                      <h3 className="font-semibold text-slate-800 truncate">{product.name}</h3>
                      {product.fileName && (
                        <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                          <i className="ri-file-3-line"></i>
                          {product.fileName}
                          {product.fileSize && (
                            <span className="text-slate-300 mx-1">•</span>
                          )}
                          {product.fileSize && <span>{product.fileSize}</span>}
                        </p>
                      )}
                      {product.purchasedAt && (
                        <p className="text-xs text-slate-400 mt-1">
                          <i className="ri-calendar-line mr-1"></i>Purchased {product.purchasedAt}
                        </p>
                      )}
                    </div>

                    {/* Download Button */}
                    <div className="flex-shrink-0 flex items-center gap-3">
                      {downloadedIds.has(product.id) && (
                        <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                          <i className="ri-check-double-line"></i>Downloaded
                        </span>
                      )}
                      <button
                        onClick={() => handleDownload(product)}
                        disabled={downloadingId === product.id}
                        className={`cursor-pointer whitespace-nowrap flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                          downloadedIds.has(product.id)
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                            : 'bg-teal-600 text-white hover:bg-teal-700'
                        } disabled:opacity-50`}
                      >
                        {downloadingId === product.id ? (
                          <>
                            <i className="ri-loader-4-line animate-spin"></i>
                            Preparing...
                          </>
                        ) : downloadedIds.has(product.id) ? (
                          <>
                            <i className="ri-download-2-line"></i>
                            Download Again
                          </>
                        ) : (
                          <>
                            <i className="ri-download-2-line"></i>
                            Download
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ) : (
          /* Empty State */
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-slate-100 rounded-2xl mb-6">
              <i className="ri-inbox-archive-line text-4xl text-slate-400"></i>
            </div>
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No downloads yet</h3>
            <p className="text-slate-400 mb-8 max-w-sm mx-auto">
              Products you purchase will appear here. Browse our collection and find something great!
            </p>
            <Link to="/products">
              <Button className="bg-teal-600 hover:bg-teal-700 text-white">
                <i className="ri-shopping-bag-line"></i>
                Browse Products
              </Button>
            </Link>
          </div>
        )}

        {/* Help Section */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              icon: 'ri-mail-send-line',
              title: 'Check Your Email',
              desc: 'Download links are automatically emailed after purchase',
              color: 'bg-teal-50 text-teal-600',
            },
            {
              icon: 'ri-time-line',
              title: 'Instant Access',
              desc: 'All files are available immediately after payment confirmation',
              color: 'bg-emerald-50 text-emerald-600',
            },
            {
              icon: 'ri-customer-service-2-line',
              title: 'Need Help?',
              desc: 'Contact our support team if you have any download issues',
              color: 'bg-slate-100 text-slate-600',
            },
          ].map((item) => (
            <Card key={item.title} className="p-5 border border-slate-200 text-center">
              <div
                className={`inline-flex items-center justify-center w-11 h-11 rounded-xl mb-3 ${item.color}`}
              >
                <i className={`${item.icon} text-xl`}></i>
              </div>
              <h4 className="font-semibold text-slate-700 mb-1">{item.title}</h4>
              <p className="text-sm text-slate-400">{item.desc}</p>
            </Card>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
}
